import { PDFDocument } from '@cantoo/pdf-lib';
import { downloadFile, Storage } from '@chocolate-soup-inc/cs-api-consumer-utils';
import { ConfirmationModal, ErrorPage, ExtendedFAB, LoadingPage, styleClassNames, TConfirmationModalProps } from '@chocolate-soup-inc/cs-frontend-components';
import { DragEndEvent, UniqueIdentifier } from '@dnd-kit/core';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { serializeError } from 'serialize-error';
import { Board } from '../../components/board/Board';
import { TColumnProps } from '../../components/board/Column';
import { TDraggableCardRenderOptions } from '../../components/board/DraggableCard';
import { Filters, TFiltersProps } from '../../components/filters/Filters';
import { FloatingActions } from '../../components/floatingActions/FloatingActions';
import { useFilter } from '../../contexts/filters';
import { isAddressValid } from '../../entities/addresses/shared';
import { useQueryDefaultGiftPackages } from '../../entities/giftPackages/queries';
import { TShipmentType, useQueryAllOpenShipmentsNEW } from '../../entities/shipments/queries';
import { filterByGiftType, filterByRecipient, generateCUSMAformPDF, matchesShipDateFilter, parseShipDateFilterValue } from '../../entities/shipments/shared';
import { TManagerAccount } from '../../entities/users/queries';
import { TShipmentStatuses, useDeleteShipmentMutation, usePrintCusmaMutation, usePrintLabelPhysicallyMutation } from '../../generated/graphql';
import { SetAsReadyToShipGropuedModal } from './groupedShipmentModal/SetAsReadyToShipGroupedModal';
import { OverrideCarrierModal } from './OverrideCarrierModal/OverrideCarrierModal';
import { PrintLabelModal } from './PrintLabelModal/PrintLabelModal';
import { PrintMultipleLabelModal } from './PrintMultipleLabelModal/PrintMultipleLabelModal';
import { SetAsReadyToShipModal } from './SetAsReadyToShipModal/SetAsReadyToShipModal';
import { ShipManuallyModal } from './ShipManuallyModal/ShipManuallyModal';
import { ShipmentCard } from './ShipmentCard/ShipmentCard';

export const columnsDetails = [
  {
    name: 'Packaging',
    destinationColumns: [TShipmentStatuses.ReadyToShip, TShipmentStatuses.ReadyForPickup],
    nextPhase: TShipmentStatuses.ReadyToShip,
    id: TShipmentStatuses.Packaging
  },
  {
    name: 'Ready to Ship',
    destinationColumns: [TShipmentStatuses.PrintingLabel, TShipmentStatuses.ReadyForPickup],
    nextPhase: TShipmentStatuses.PrintingLabel,
    id: TShipmentStatuses.ReadyToShip
  },
  {
    name: 'Printing Label',
    destinationColumns: [],
    nextPhase: undefined,
    id: TShipmentStatuses.PrintingLabel
  },
  {
    name: 'Printed Label',
    destinationColumns: [],
    nextPhase: undefined,
    id: TShipmentStatuses.LabelPrinted
  }
];

type TGiftTypeFilter = 'subscription' | 'newHire' | 'one-off';
type TAddressTypeFilter = 'employee' | 'office' | 'company';

export const ShipmentsNEW = () => {
  const { filtersPageMode } = useFilter();
  useEffect(() => {
    filtersPageMode();
  }, []); //eslint-disable-line

  const [companyFilter, setCompanyFilter] = useState<string>();
  const [recipientFilter, setRecipientFilter] = useState<string>();
  const [clientManagerFilter, setClientManagerFilter] = useState<string>();
  const [giftTypeFilter, setGiftTypeFilter] = useState<TGiftTypeFilter>();
  const [addressTypeFilter, setAddressTypeFilter] = useState<TAddressTypeFilter>();
  const [trackingNumberFilter, setTrackingNumberFilter] = useState<string>();
  const [shipDateFilter, setShipDateFilter] = useState<Date>();

  const { data: shipments, error, loading } = useQueryAllOpenShipmentsNEW();

  const [shipManuallyModal, setShipManuallyModal] = useState<boolean>(false);
  const [shipManuallyShipment, setShipManuallyShipment] = useState<TShipmentType>();
  const [groupableByAddressModal, setGroupableByAddressModal] = useState<boolean>(false);
  const [groupableByAddressShipment, setGroupableByAddressShipment] = useState<TShipmentType>();
  const [carrierOverrideModal, setCarrierOverrideModal] = useState<boolean>(false);
  const [carrierOverrideShipment, setCarrierOverrideShipment] = useState<TShipmentType>();
  const [deleteConfirmationProps, setDeleteConfirmationProps] = useState<Omit<TConfirmationModalProps, 'closeModal' | 'confirmLoading' | 'onCancelClick'>>();

  const { data: defaultPackages, loading: defaultPackagesLoading, error: defaultPackagesError } = useQueryDefaultGiftPackages();

  // If data changes while the modal is active, keep the state up to date
  useEffect(() => {
    if (!shipManuallyShipment) return;
    const { id: shipmentManuallyId } = shipManuallyShipment;
    const newShipManuallyShipment = shipments.find((s) => {
      return s.id === shipmentManuallyId;
    });
    setShipManuallyShipment(newShipManuallyShipment);
  }, [shipments, shipManuallyShipment]);

  const companies = useMemo(() => {
    return _.uniqBy(_.compact(shipments.map((s) => s.company)), 'id');
  }, [shipments]);

  const clientManagers = useMemo(() => {
    return _.unionBy(
      shipments.reduce<TManagerAccount[]>((acc, s) => {
        const manager = s.company?.accountManager;
        if (manager && typeof manager === 'string') acc.push({ name: manager, id: manager });

        return acc;
      }, []),
      'id'
    );
  }, [shipments]);

  const filters = useMemo(() => {
    const filtersList: TFiltersProps['filters'] = [];

    filtersList.push({
      disabled: companies.length === 0,
      label: 'Company',
      name: 'companyId',
      onChange: (v) => {
        setCompanyFilter(v != null ? (v as string) : undefined);
      },
      options: companies?.map((c) => ({ label: c.name, value: c.id })),
      type: 'singleSelect',
      value: companyFilter
    });

    filtersList.push({
      label: 'Recipient',
      name: 'recipientName',
      onChange: (v) => {
        setRecipientFilter(v != null ? v : undefined);
      },
      type: 'textInput',
      value: recipientFilter
    });

    filtersList.push({
      disabled: clientManagers.length === 0,
      label: 'Client Manager',
      name: 'clientManager',
      onChange: (v) => {
        setClientManagerFilter(v != null ? (v as string) : undefined);
      },
      options: clientManagers.map((c) => ({ label: c.name, value: c.id })),
      type: 'singleSelect',
      value: clientManagerFilter
    });

    filtersList.push({
      label: 'Gift Type',
      name: 'giftType',
      onChange: (v) => {
        setGiftTypeFilter(v != null ? (v as TGiftTypeFilter) : undefined);
      },
      type: 'singleSelect',
      options: [
        {
          label: 'Subscription',
          value: 'subscription'
        },
        {
          label: 'New Hire',
          value: 'newHire'
        },
        {
          label: 'One Off',
          value: 'one-off'
        }
      ],
      value: giftTypeFilter
    });

    filtersList.push({
      label: 'Address Type',
      name: 'addressType',
      onChange: (v) => {
        setAddressTypeFilter(v != null ? (v as TAddressTypeFilter) : undefined);
      },
      type: 'singleSelect',
      options: [
        {
          label: 'Employee',
          value: 'employee'
        },
        {
          label: 'Office',
          value: 'office'
        },
        {
          label: 'Company',
          value: 'company'
        }
      ],
      value: addressTypeFilter
    });

    filtersList.push({
      label: 'Tracking Number',
      name: 'trackingNumber',
      onChange: (v) => {
        setTrackingNumberFilter(v != null ? (v as string) : undefined);
      },
      type: 'textInput',
      value: trackingNumberFilter
    });

    filtersList.push({
      type: 'datePicker',
      label: 'Shipping Date',
      name: 'shipDate',
      value: shipDateFilter,
      onChange: (v) => {
        const parsed = parseShipDateFilterValue(v);
        setShipDateFilter((prev) => (prev?.getTime() === parsed?.getTime() ? prev : parsed));
      }
    });

    return filtersList;
  }, [companies, companyFilter, giftTypeFilter, addressTypeFilter, recipientFilter, trackingNumberFilter, shipDateFilter, clientManagerFilter, clientManagers]);

  const filteredShipments = useMemo(() => {
    return shipments.filter((s) => {
      if (companyFilter && s.companyId !== companyFilter) return false;

      if (trackingNumberFilter && !s.trackingNumber?.toLowerCase().startsWith(trackingNumberFilter.toLowerCase())) return false;

      if (addressTypeFilter && s.address?.parentType != addressTypeFilter) return false;

      if (shipDateFilter && !matchesShipDateFilter(s, shipDateFilter)) return false;

      if (clientManagerFilter != undefined && clientManagerFilter != '' && clientManagerFilter != s.company?.accountManager) return false;

      return (
        filterByRecipient({
          shipment: s,
          filter: recipientFilter
        }) &&
        filterByGiftType({
          shipment: s,
          filter: giftTypeFilter
        })
      );
    });
  }, [shipments, companyFilter, trackingNumberFilter, recipientFilter, giftTypeFilter, addressTypeFilter, shipDateFilter, clientManagerFilter]);

  //console.log('shipments NEW?:', shipments);
  console.log('filteredShipments NEW?:', filteredShipments);

  const groupedByStatusShipments = useMemo(() => {
    return _.groupBy(filteredShipments, (s) => s.status);
  }, [filteredShipments]);

  const sortedGroupedByStatusShipments = useMemo(() => {
    return Object.entries(groupedByStatusShipments).reduce(
      (agg, [key, statusShipments]) => {
        agg[key] = _.orderBy(statusShipments, [(s) => new Date(s.shippingDate as string).getTime(), 'name'], ['desc', 'asc']);
        return agg;
      },
      {} as Record<string, TShipmentType[]>
    );
  }, [groupedByStatusShipments]);

  const renderShipment = useCallback((shipment: TShipmentType, opts?: TDraggableCardRenderOptions) => {
    return (
      <ShipmentCard
        key={shipment.id}
        shipment={shipment}
        options={opts}
        onShipManually={() => {
          setShipManuallyModal(true);
          setShipManuallyShipment(shipment);
        }}
        onGroupable={() => {
          setGroupableByAddressModal(true);
          setGroupableByAddressShipment(shipment);
        }}
        onCarrierOverride={() => {
          setCarrierOverrideModal(true);
          setCarrierOverrideShipment(shipment);
        }}
      />
    );
  }, []);

  const columns: TColumnProps<TShipmentType>[] = useMemo(() => {
    return columnsDetails.map((columnDetails) => {
      const status = columnDetails.id;
      let groupableAddresses: string[] = [];

      let groupedAddresses: Record<string, any>;
      if (status === TShipmentStatuses.Packaging) {
        groupedAddresses = _.groupBy(sortedGroupedByStatusShipments[status] || [], (s) => s.addressId);

        groupableAddresses = Object.entries(groupedAddresses)
          // eslint-disable-next-line
          .filter(([key, value]: any) => value.length >= 2)
          .map(([key]) => key);
      }

      let cards: TColumnProps<TShipmentType>['cards'] = (sortedGroupedByStatusShipments[status] || []).map((shipment) => {
        const groupable = groupableAddresses.includes(shipment.addressId) && status === TShipmentStatuses.Packaging;

        return {
          data: groupable ? { ...shipment, groupable, groupableItems: groupedAddresses[shipment.addressId] } : shipment,
          disabled: _.isEmpty(columnDetails.destinationColumns),
          id: shipment.id,
          render: renderShipment
        };
      });

      cards = _.orderBy(cards, [(c) => new Date(c.data.actualShippingDate ?? (c.data.shippingDate as string)).getTime(), (c) => c.data.recipientName?.toLowerCase()], ['desc', 'asc']);

      return {
        cards,
        data: {
          destinationColumns: columnDetails.destinationColumns || []
        },
        id: columnDetails.id,
        name: columnDetails.name
      };
    });
  }, [renderShipment, sortedGroupedByStatusShipments]);

  const [shipmentsGoingToReadyToShip, setShipmentsGoingToReadyToShip] = useState<TShipmentType[]>();
  const [shipmentsGoingToLabelPrinted, setShipmentsGoingToLabelPrinted] = useState<TShipmentType[]>();

  /*const updateShipmentFragment = useCallback((shipment: TShipmentType, status: TShipmentStatuses) => {
    cache.writeFragment({
      data: {
        ...shipment,
        status,
      },
      id: cache.identify(shipment),
      fragment: ShipmentFieldsFragmentDoc,
      fragmentName: getFragmentName(ShipmentFieldsFragmentDoc),
    });
  }, []);*/

  const onDragEnd = useCallback((event: DragEndEvent, selectedShipments: TShipmentType[]) => {
    const droppedColumnId = event.over?.id as TShipmentStatuses | undefined;

    if (droppedColumnId === TShipmentStatuses.ReadyToShip) {
      const invalidAddresses: Record<string, string> = {};

      const allAddressComplete = selectedShipments.every((s: TShipmentType) => {
        const address = {
          ...s.address,
          useGoogleAddress: s.useGoogleAddress || s.address?.useGoogleAddress
        };
        const addressValid = isAddressValid(address);
        if (!addressValid) {
          invalidAddresses[s.id] = `${s.recipientName} at ${s.company?.name}`;
          return false;
        }
        return addressValid;
      });

      if (!allAddressComplete) {
        console.warn('INVALID ADDRESSES (moving to Ready To Ship)', invalidAddresses);
        return toast.error('Some shipments have incomplete addresses. Check the web console for more details.');
      }

      setShipmentsGoingToReadyToShip(selectedShipments);
    } else if (droppedColumnId != null && [TShipmentStatuses.LabelPrinted, TShipmentStatuses.PrintingLabel].includes(droppedColumnId))
      //for (const shipment of selectedShipments) updateShipmentFragment(shipment, TShipmentStatuses.PrintingLabel);
      setShipmentsGoingToLabelPrinted(selectedShipments);
  }, []);

  const onMoveToReadyToShipCancel = useCallback(() => {
    if (shipmentsGoingToReadyToShip)
      //for (const shipment of shipmentsGoingToReadyToShip) updateShipmentFragment(shipment, shipment.status);
      setShipmentsGoingToReadyToShip(undefined);
  }, [shipmentsGoingToReadyToShip]);

  const onMoveToReadyToShipSuccess = useCallback(() => {
    setShipmentsGoingToReadyToShip(undefined);
  }, []);

  const onMoveToLabelPrintedCancel = useCallback(() => {
    if (shipmentsGoingToLabelPrinted)
      //for (const shipment of shipmentsGoingToLabelPrinted) updateShipmentFragment(shipment, shipment.status);
      setShipmentsGoingToLabelPrinted(undefined);
  }, [shipmentsGoingToLabelPrinted]);

  const onMoveToLabelPrintedSuccess = useCallback(() => {
    setShipmentsGoingToLabelPrinted(undefined);
  }, []);

  const [selectedShipments, setSelectedShipments] = useState<TShipmentType[]>([]);

  const onSelectedCardsChange = useCallback(
    (selectedCardsIds: UniqueIdentifier[]) => {
      setSelectedShipments(shipments.filter((s) => selectedCardsIds.includes(s.id)));
    },
    [shipments]
  );

  const shipmentCanBePrinted = useCallback((shipment: TShipmentType) => {
    return shipment.label?.labelFile && shipment.label.labelFile.bucket != null && shipment.label.labelFile.key != null && shipment.label.labelFile.region != null;
  }, []);

  const allSelectedCanBePrinted = useMemo(() => {
    for (const shipment of selectedShipments) if (!shipmentCanBePrinted(shipment)) return false;

    return true;
  }, [selectedShipments, shipmentCanBePrinted]);

  const selectedCanBeCUSMAPrinted = useMemo(
    () =>
      selectedShipments.filter(
        (s) =>
          [
            //TShipmentStatuses.ReadyToShip,
            // TShipmentStatuses.PrintingLabel,
            TShipmentStatuses.LabelPrinted,
            TShipmentStatuses.Shipped
          ].includes(s.status) && ['usa'].includes(s.address?.country?.toLowerCase() as string)
      ).length,
    [selectedShipments]
  );

  const [printAllLoading, setPrintAllLoading] = useState<boolean>(false);
  const [deletedAllLoading, setDeletedAllLoading] = useState<boolean>(false);

  const [printLabelPhysically] = usePrintLabelPhysicallyMutation({});
  const [printCUSMA] = usePrintCusmaMutation({});
  const [deleteShipment] = useDeleteShipmentMutation();

  const onDeletedAllClick = useCallback(async () => {
    setDeletedAllLoading(true);
    let recordId: string | undefined;
    try {
      for (const shipment of selectedShipments) {
        recordId = shipment.id;
        await deleteShipment({ variables: { companyId: shipment.companyId, id: shipment.id, version: shipment._version } });
        toast.success(`Shipment for ${shipment.recipientName} has been deleted successfully.`);
      }
      setDeleteConfirmationProps(undefined);
    } catch (error) {
      console.error('Shipment deletion failed', { shipmentId: recordId, error: serializeError(error) });
      toast.error('There was an error trying to delete the selected shipments.');
    } finally {
      setDeletedAllLoading(false);
    }
  }, [selectedShipments, deleteShipment]);

  const onDeleteClick = useCallback(() => {
    setDeleteConfirmationProps({
      headline: 'Remove Shipments',
      supportingText: `You are going to remove ${selectedShipments.length} shipment${selectedShipments.length > 1 ? 's' : ''}. Are you sure?`,
      confirmLabel: 'Remove',
      onConfirmClick: () => onDeletedAllClick()
    });
  }, [onDeletedAllClick, setDeleteConfirmationProps, selectedShipments]);

  const onPrintCusmaClick = useCallback(async () => {
    let recordId: string | undefined;
    try {
      const trackingList: string[] = [];
      let yougestShipDate = new Date();

      const orderedShipments = selectedShipments.sort((a, b) => {
        const x = new Date(a.actualShippingDate as string);
        const y = new Date(b.actualShippingDate as string);
        return x.getTime() - y.getTime();
      });
      for (const shipment of orderedShipments) {
        if (
          ![
            //TShipmentStatuses.ReadyToShip,
            //TShipmentStatuses.PrintingLabel,
            TShipmentStatuses.LabelPrinted
          ].includes(shipment.status) ||
          !['USA'].includes(shipment.address?.country as string)
        )
          continue;
        recordId = shipment.id;
        await generateCUSMAformPDF(shipment);
        await printCUSMA({
          variables: { companyId: shipment.companyId, id: shipment.id, version: shipment._version }
        });
        trackingList.push(shipment.trackingNumber as string);
        const currentShipDate = new Date(shipment.actualShippingDate || shipment.shippingDate || yougestShipDate);
        if (currentShipDate.getTime() < yougestShipDate.getTime()) yougestShipDate = currentShipDate;
      }
      downloadFile({
        data: [trackingList.join('\n')],
        filetype: 'text/plain',
        filename: `${orderedShipments[0].actualShippingDate} - CUSMA.txt`
        //filename: `${readableDate(yougestShipDate, 'dd-MM-yyyy_HH-mm')} - CUSMA.txt`,
      });
    } catch (error) {
      console.error('Shipment CUSMA printing failed', { shipmentId: recordId, error: serializeError(error) });
      toast.error('There was an error trying to print the selected CUSMA forms.');
    }
  }, [selectedShipments, printCUSMA]);

  const onPrintAllClick = useCallback(async () => {
    setPrintAllLoading(true);
    let recordId: string | undefined;
    try {
      const pdfDoc = await PDFDocument.create();
      for (const shipment of [...selectedShipments].sort((a, b) => a.id.localeCompare(b.id))) {
        recordId = shipment.id;
        const signedURL = await Storage.get(shipment.label?.labelFile?.key as string, {
          bucket: shipment.label?.labelFile?.bucket,
          region: shipment.label?.labelFile?.region
        });
        const response = await fetch(signedURL);
        const doc = await PDFDocument.load(await response.arrayBuffer());
        await printLabelPhysically({ variables: { companyId: shipment.companyId, id: shipment.id, version: shipment._version } });
        const copied = await pdfDoc.copyPages(doc, doc.getPageIndices());
        for (const page of copied) pdfDoc.addPage(page);
      }
      downloadFile({ data: [await pdfDoc.save()] as BlobPart[], filetype: 'application/pdf', filename: 'print-labels.pdf' });
    } catch (error) {
      console.error('Shipment label printing failed', { shipmentId: recordId, error: serializeError(error) });
      toast.error('There was an error trying to print the selected labels.');
    } finally {
      setPrintAllLoading(false);
    }
  }, [selectedShipments, printLabelPhysically]);

  if (loading || defaultPackagesLoading) return <LoadingPage />;

  if (error || defaultPackagesError) {
    console.error('query Apollo ERROR:', error);
    console.error('query Apollo ERROR:', defaultPackagesError);
    toast.error('An error has ocurred, check the console for details. (query Apollo ERROR)');
    //if (error.message) toast.error(error.message);
    //if (defaultPackagesError.message) toast.error(defaultPackagesError.message);
    if (!shipments) return <ErrorPage error={error || defaultPackagesError} />;
  }

  return (
    <>
      {!_.isEmpty(deleteConfirmationProps) && (
        <ConfirmationModal {...deleteConfirmationProps} closeModal={() => setDeleteConfirmationProps(undefined)} confirmLoading={deletedAllLoading} onCancelClick={() => setDeleteConfirmationProps(undefined)} />
      )}
      {shipManuallyModal && shipManuallyShipment && (
        <ShipManuallyModal
          shipment={shipManuallyShipment}
          onCancel={() => {
            setShipManuallyModal(false);
          }}
          onSuccess={() => {
            setShipManuallyModal(false);
          }}
        />
      )}
      {groupableByAddressModal && groupableByAddressShipment && (
        <SetAsReadyToShipGropuedModal
          shipments={(groupableByAddressShipment.groupableItems || []) as unknown as TShipmentType[]}
          onCancel={() => {
            setGroupableByAddressModal(false);
            setGroupableByAddressShipment(undefined);
          }}
          onSuccess={() => {
            setGroupableByAddressModal(false);
            setGroupableByAddressShipment(undefined);
            toast.success(`Shipments grouped succesfully.`);
          }}
        />
      )}
      {carrierOverrideModal && carrierOverrideShipment && (
        <OverrideCarrierModal
          shipment={carrierOverrideShipment}
          onCancel={() => {
            setCarrierOverrideModal(false);
            setCarrierOverrideShipment(undefined);
          }}
          onSuccess={() => {
            setCarrierOverrideModal(false);
            setCarrierOverrideShipment(undefined);
            toast.success(`Shipment carrier overridden succesfully.`);
          }}
        />
      )}
      {Array.isArray(shipmentsGoingToReadyToShip) && shipmentsGoingToReadyToShip.length > 0 && <SetAsReadyToShipModal shipments={shipmentsGoingToReadyToShip} onCancel={onMoveToReadyToShipCancel} onSuccess={onMoveToReadyToShipSuccess} />}
      {Array.isArray(shipmentsGoingToLabelPrinted) && shipmentsGoingToLabelPrinted.length > 1 && (
        <PrintMultipleLabelModal shipments={shipmentsGoingToLabelPrinted} onCancel={onMoveToLabelPrintedCancel} onSuccess={onMoveToLabelPrintedSuccess} defaultPackages={defaultPackages} />
      )}
      {Array.isArray(shipmentsGoingToLabelPrinted) && shipmentsGoingToLabelPrinted.length === 1 && (
        <PrintLabelModal shipment={shipmentsGoingToLabelPrinted[0]} onCancel={onMoveToLabelPrintedCancel} onSuccess={onMoveToLabelPrintedSuccess} defaultPackages={defaultPackages} />
      )}
      <p>(NEW)</p>
      <div className={styleClassNames.fullHeightColumnPage}>
        <Filters filters={filters} />
        <Board<TShipmentType> columns={columns} onDragEnd={onDragEnd} onSelectedCardsChange={onSelectedCardsChange} />
        {selectedShipments.length > 0 && (
          <FloatingActions>
            {allSelectedCanBePrinted && (
              <ExtendedFAB
                // disabled={printAllLoading}
                label={`Print ${selectedShipments.length} label${selectedShipments.length > 1 ? 's' : ''}`}
                leadingIcon='print'
                loading={printAllLoading}
                onClick={onPrintAllClick}
                variant='surface'
              />
            )}
            {!!selectedCanBeCUSMAPrinted && (
              <ExtendedFAB
                // disabled={printAllLoading}
                label={`Print ${selectedCanBeCUSMAPrinted} CUSMA (USA) form${selectedShipments.length > 1 ? 's' : ''} (UPS)`}
                leadingIcon='print'
                loading={printAllLoading}
                onClick={onPrintCusmaClick}
                variant='secondary'
              />
            )}
            <ExtendedFAB
              // disabled={printAllLoading}
              label={`Delete ${selectedShipments.length} shipment${selectedShipments.length > 1 ? 's' : ''}`}
              leadingIcon='delete'
              loading={deletedAllLoading}
              onClick={onDeleteClick}
              variant='tertiary'
            />
          </FloatingActions>
        )}
      </div>
    </>
  );
};
